import React, { useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { saveSFQInfo, fetchSFQDetail } from "../Redux/feasibility";

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
  font-size: 13px;
  color: #6c757d;
`;

const State = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  background: ${(p) => (p.$on ? "#e7f5ec" : "#f1f3f5")};
  color: ${(p) => (p.$on ? "#15803d" : "#6c757d")};
`;

const LinkBtn = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 12px;
  color: #0d6efd;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { text-decoration: underline; }
`;

const Muted = styled.span`
  color: #adb5bd;
  font-size: 12px;
`;

// 끈 뒤에도 값은 남는다 — 감추기만 하므로 다시 켜면 그대로 돌아온다.
const OFF_CONFIRM =
  "이 Study의 모든 사이트에서 Assessment를 사용하지 않습니다.\n" +
  "초기·최종 평가 탭, 점수·리스크 표시, 내보내기(PDF/Word)의 평가 섹션이 모두 사라집니다.\n\n" +
  "이미 입력된 평가 내용은 삭제되지 않으며, 다시 [사용]으로 바꾸면 그대로 나타납니다.\n" +
  "계속하시겠습니까?";

const ON_CONFIRM =
  "이 Study에서 Assessment를 다시 사용합니다.\n" +
  "이전에 입력된 평가·점수·리스크가 있다면 그대로 다시 표시됩니다.\n" +
  "계속하시겠습니까?";

// Whether the study runs the CRA assessment at all. Takes anything carrying the study's
// flag — the study detail, or an invitation detail, which serves it alongside the study's
// owner and reviewer. Missing is read as on: studies predate the switch (migration 035
// defaults to 1), and so may a response still in flight when the frontend deploys.
export const craAssessmentEnabled = (row) =>
  row?.craAssessmentEnabled === undefined ? true : !!row.craAssessmentEnabled;

// Study 단위 CRA Assessment 사용 여부. 스터디 설정이므로 Owner(와 Admin)만 바꿀 수 있고,
// Locked/Archived에서는 나머지 스터디 정보와 함께 잠긴다 — canEdit이 그 판단을 넘겨받는다.
// API도 같은 규칙을 강제하며, 꺼진 스터디의 평가 저장은 422로 거절한다.
export default function CraAssessmentToggle({ sfq, canEdit }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const enabled = craAssessmentEnabled(sfq);

  async function toggle() {
    const next = !enabled;
    if (!window.confirm(next ? ON_CONFIRM : OFF_CONFIRM)) return;
    setSaving(true);
    try {
      await dispatch(
        saveSFQInfo({ id: sfq.id, body: { craAssessmentEnabled: next } })
      ).unwrap();
      await dispatch(fetchSFQDetail(sfq.id));
    } catch (e) {
      alert(e?.message || "Assessment 사용 여부 변경에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row>
      <span>Assessment:</span>
      <State $on={enabled}>{enabled ? "사용" : "사용 안 함"}</State>
      {canEdit && (
        <LinkBtn disabled={saving} onClick={toggle}>
          {enabled ? "사용 안 함으로 변경" : "사용으로 변경"}
        </LinkBtn>
      )}
      {!enabled && (
        <Muted>사이트 응답만 수집하며, 평가·점수·리스크는 사용하지 않습니다.</Muted>
      )}
      {saving && <Muted>저장 중...</Muted>}
    </Row>
  );
}
